import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { ClientStore } from '@shopexperts/shared';

/**
 * Composable for fetching the authenticated client's stores.
 * 
 * Used in the lead submission form to allow clients to select
 * an existing store instead of creating a new one.
 * 
 * @param enabled - Whether the query should be enabled (typically based on auth status)
 * 
 * @example
 * ```ts
 * const { stores, isLoading } = useClientStores(isAuthenticatedClient);
 * ```
 */
export function useClientStores(enabled: ComputedRef<boolean>) {
  const api = useApi();

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['client', 'stores'],
    queryFn: async () => {
      console.log('[useClientStores] Fetching stores...');
      const response = await api.client.storesV2.listStores({
        query: {
          limit: 50,
        },
      });
      console.log('[useClientStores] Response:', response);
      return response.data;
    },
    enabled,
    staleTime: 30000,
  });

  // data.value is already ClientStore[] | undefined
  const stores = computed<ClientStore[]>(() => {
    console.log('[useClientStores] Computed stores:', data.value);
    return data.value ?? [];
  });

  return {
    stores,
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
}
